import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { toast } from '@codes/ui-kit/components/ui/use-toast';

import type { CreateChildContentDTO } from '@codes/box-contents';
import { useCreateChildContent } from '@codes/box-contents';
import { useLoader } from '@codes/loader';

import { useContentCache } from './useContentCache';

import type { ContentView } from '@lemoncloud/eureka-contents-api';

export const useCreateChildContentWithCache = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { prependContentToCache } = useContentCache();
    const createChildContent = useCreateChildContent();
    const { setIsLoading } = useLoader();

    const handleCreateChild = useCallback(
        async (parentId: string) => {
            setIsLoading(true);
            const newChildContent: CreateChildContentDTO = {
                name: '',
                title: '',
                subject: '',
                parentId,
            };

            try {
                await createChildContent.mutateAsync(newChildContent, {
                    onSuccess: async (response: ContentView) => {
                        await prependContentToCache(response);
                        navigate(`/box/${response.id}`);
                    },
                });
            } catch (e) {
                toast({ description: t('box.generalError'), variant: 'destructive' });
            } finally {
                setIsLoading(false);
            }
        },
        [createChildContent, navigate, prependContentToCache, t]
    );

    return {
        handleCreateChild,
        isPending: createChildContent.isPending,
    };
};
